import { useEffect, useMemo, useState } from 'react';

import { AemImageSizes } from './types';

export interface AemResponsiveImageBreakpoint {
    imageSize: keyof AemImageSizes;
    maxViewportWidth: number;
}

interface SelectedImage {
    rank: number;
    url: string;
}

const RESIZE_DEBOUNCE_DELAY = 150;

const selectImage = (
    sizes: AemImageSizes | undefined,
    breakpoints: ReadonlyArray<AemResponsiveImageBreakpoint>,
    viewportWidth: number,
): SelectedImage | undefined => {
    if (!sizes || breakpoints.length === 0) {
        return undefined;
    }

    const matchingRank = breakpoints.findIndex(({ maxViewportWidth }) => viewportWidth <= maxViewportWidth);
    const desiredRank = matchingRank === -1 ? breakpoints.length - 1 : matchingRank;

    for (let rank = desiredRank; rank < breakpoints.length; rank += 1) {
        const url = sizes[breakpoints[rank].imageSize];

        if (url) {
            return { rank, url };
        }
    }

    for (let rank = desiredRank - 1; rank >= 0; rank -= 1) {
        const url = sizes[breakpoints[rank].imageSize];

        if (url) {
            return { rank, url };
        }
    }

    return undefined;
};

export const useAemResponsiveImageSource = (
    sizes: AemImageSizes | undefined,
    breakpoints: ReadonlyArray<AemResponsiveImageBreakpoint>,
): string | undefined => {
    const [viewportWidth, setViewportWidth] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth : Number.POSITIVE_INFINITY,
    );
    const desiredImage = useMemo(() => selectImage(sizes, breakpoints, viewportWidth), [
        breakpoints,
        sizes,
        viewportWidth,
    ]);
    const [selectedImage, setSelectedImage] = useState(desiredImage);

    useEffect(() => {
        let resizeTimeout: ReturnType<typeof setTimeout>;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => setViewportWidth(window.innerWidth), RESIZE_DEBOUNCE_DELAY);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(resizeTimeout);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const isCurrentImageFromSameSet = selectedImage && Object.values(sizes ?? {}).includes(selectedImage.url);

        if (!isCurrentImageFromSameSet || !selectedImage) {
            setSelectedImage(desiredImage);
            return;
        }

        if (!desiredImage || desiredImage.rank <= selectedImage.rank) {
            return;
        }

        const image = new Image();
        image.onload = () => setSelectedImage(desiredImage);
        image.src = desiredImage.url;

        return () => {
            image.onload = null;
        };
    }, [desiredImage, selectedImage, sizes]);

    return selectedImage?.url;
};
